import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TErrorInStore,
  TIdentificationForm,
  TIdentificationStore,
  TUpdateFormResponse,
} from './types';
import { initialFormState, initialIdentificationState } from './initialState';
import {
  createApplication,
  fetchCreatedIdentificationForm,
  fetchCreatedIdentificationFormAssetManager,
  fetchIdentificationForm,
  generateDocs,
  getAssetManagerDocs,
  getIsAssetManagersChanged,
  infoByIban,
  managerCreateApplication,
  updateForm,
  updateFormAssetManager,
  updateFormChangeVideoVerification,
  updateFormFatcaAdditionalInfo,
  videoVerificationAssetManagers,
} from './thunks/thunks';
import {
  setErrorAction,
  setLoadingAction,
  updateAdditionalInfoFulfilledAction,
  setFormData,
  validateBeforeSigningFulfilled,
  setApplicationId,
  setIsAssetManagersChanged,
  getAssetManagerDocsFulfilled,
  setMainDocuments,
  fulfilledAction,
  setFormDataChangeVideoVerification,
  setFormFatcaAdditionalInfo,
} from './actions/actions';
import {
  addDocument,
  checkRegenerateDocs,
  deleteDocument,
  deleteDocumentAssetManager,
  deleteDocumentsInvalidSignatures,
  deleteDocumentsInvalidSignaturesAssetManager,
  getDocumentTemplates,
  getSignatureEmail,
  regenerateReports,
  validateBeforeSigning,
  validateDocumentSign,
  validateDocumentSignAssetManager,
} from './thunks/documentThunks';
import {
  addDocumentFulfilledAction,
  deleteDocumentFulfilledAction,
  defaultFulfilledAction,
  documentsError,
  validateDocumentSignAction,
} from './actions/documentActions';
import {
  taxResidencyAdditionalInfoDelete,
  taxResidencyAdditionalInfoPrefill,
  updateAdditionalInformation,
} from './thunks/additionalInformationThunks';
import { TGettingStarted } from '~/modules/identification/forms/GettingStarted/types';
import {
  applicationReportActionPending,
  applicationReportFulfilled,
  applicationReportActionFulfilled,
  applicationReportActionRejected,
  getApplicationReportExcelFulfilled,
  taxResidencyPrefillFulfilled,
} from '~/store/identification-form/actions/applicationReportActions.ts';
import {
  applicationReport,
  archiveApplicationReport,
  getApplicationReportExcel,
  restoreApplicationReport,
} from '~/store/identification-form/thunks/applicationReportThunks.ts';
import { TApplicationReportsForm } from '~/components/ApplicationReports/types.ts';
import { TDocumentsInStore } from '~/modules/identification/forms/Documents/types';
import {
  b2IntegrationAssetManagersPrefill,
  updateDataProcessingAcceptedAssetManagers,
  getManagementStructureSyncAssetManagers,
} from './thunks/assetManagersThunk';
import { EMPTY_ERROR } from '~/constants/errorCodes';
import { setManagementStructureSyncAssetManagers } from '~/store/identification-form/actions/assetManagersActions.ts';
import {
  ManagerVerificationStatus,
  TAssetManager,
  TManagersAndRepresentatives,
} from '~/modules/identification/forms/ManagersAndRepresentatives/types';

const identificationFormSlice = createSlice({
  name: 'identification',
  initialState: initialIdentificationState,
  reducers: {
    clearForm(state: TIdentificationStore) {
      state.formData = initialFormState;
      state.formDataBack = initialFormState;
      state.error = EMPTY_ERROR;
      state.loading = false;
    },
    setIdentificationError(state: TIdentificationStore, action: PayloadAction<TErrorInStore>) {
      state.error = action.payload;
      state.loading = false;
    },
    setIdentificationAppId(state: TIdentificationStore, action: PayloadAction<number | undefined>) {
      state.formData.applicationId = action.payload;
    },
    setIdentificationLoading(state: TIdentificationStore, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setGettingStarted(state: TIdentificationStore, action: PayloadAction<TGettingStarted>) {
      (state.formData.gettingStarted as TGettingStarted) = {
        ...state.formData.gettingStarted,
        ...action.payload,
      };
    },
    setIsReidentification(state: TIdentificationStore, action: PayloadAction<boolean>) {
      state.isReidentification = action.payload;
    },
    setApplicationReportTable(
      state: TIdentificationStore,
      action: PayloadAction<TApplicationReportsForm>
    ) {
      state.applicationReportTable = action.payload;
    },
    setApplicationExportToExcel(
      state: TIdentificationStore,
      action: PayloadAction<TApplicationReportsForm>
    ) {
      state.applicationExportToExcel = action.payload;
    },
    setDisabledDiagramGeneration(state: TIdentificationStore, action: PayloadAction<boolean>) {
      state.isDisabledDiagramGeneration = action.payload;
    },
    updateDocsInFormAction(state, action: PayloadAction<TUpdateFormResponse>) {
      setMainDocuments(state, action);
    },
    updateFormDataBack(
      state: TIdentificationStore,
      action: PayloadAction<{
        field: keyof TIdentificationForm;
        data: TIdentificationForm[keyof TIdentificationForm];
      }>
    ) {
      state.formDataBack &&
        (state.formDataBack[action.payload.field] = action.payload.data as never);
    },
    updateFormData(
      state: TIdentificationStore,
      action: PayloadAction<{
        field: keyof TIdentificationForm;
        data: TIdentificationForm[keyof TIdentificationForm];
      }>
    ) {
      const { field, data } = action.payload;
      const current = state.formData[field];

      if (current && typeof current === 'object' && typeof data === 'object') {
        Object.assign(current as object, data);
      } else {
        state.formData[field] = data as never;
      }
    },
    deleteAllAssetManagerDocs(state: TIdentificationStore) {
      (state.formData.documents as TDocumentsInStore).documents.assetManagersDocuments = [];
    },
    updateAssetManagerVerificationStatus(
      state: TIdentificationStore,
      action: PayloadAction<{
        index: number;
        status: ManagerVerificationStatus;
      }>
    ) {
      const index = action.payload.index;
      (
        (
          (state.formData.managersAndRepresentatives as TManagersAndRepresentatives)
            .assetManagers as TAssetManager[]
        )[index] as TAssetManager
      ).verificationStatus = action.payload.status;
    },
    deleteAssetManagerDocsById(state: TIdentificationStore, action: PayloadAction<string | null>) {
      const docs = (state.formData.documents as TDocumentsInStore).documents.assetManagersDocuments;
      (state.formData.documents as TDocumentsInStore).documents.assetManagersDocuments =
        docs.filter((item) => item.managerId !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIdentificationForm.pending, setLoadingAction)
      .addCase(fetchIdentificationForm.fulfilled, setFormData)
      .addCase(fetchIdentificationForm.rejected, setErrorAction)

      .addCase(createApplication.fulfilled, setApplicationId)
      .addCase(createApplication.rejected, setErrorAction)

      .addCase(managerCreateApplication.fulfilled, setApplicationId)
      .addCase(managerCreateApplication.rejected, setErrorAction)

      .addCase(videoVerificationAssetManagers.pending, setLoadingAction)
      .addCase(videoVerificationAssetManagers.fulfilled, setFormData)
      .addCase(videoVerificationAssetManagers.rejected, setErrorAction)

      .addCase(fetchCreatedIdentificationFormAssetManager.pending, setLoadingAction)
      .addCase(fetchCreatedIdentificationFormAssetManager.fulfilled, setFormData)
      .addCase(fetchCreatedIdentificationFormAssetManager.rejected, setErrorAction)

      .addCase(updateForm.pending, setLoadingAction)
      .addCase(updateForm.fulfilled, fulfilledAction)
      .addCase(updateForm.rejected, setErrorAction)

      .addCase(updateFormFatcaAdditionalInfo.pending, setLoadingAction)
      .addCase(updateFormFatcaAdditionalInfo.fulfilled, setFormFatcaAdditionalInfo)
      .addCase(updateFormFatcaAdditionalInfo.rejected, setErrorAction)

      .addCase(updateFormAssetManager.pending, setLoadingAction)
      .addCase(updateFormAssetManager.fulfilled, setMainDocuments)
      .addCase(updateFormAssetManager.rejected, setErrorAction)

      .addCase(getIsAssetManagersChanged.fulfilled, setIsAssetManagersChanged)
      .addCase(getIsAssetManagersChanged.rejected, setErrorAction)

      .addCase(addDocument.fulfilled, addDocumentFulfilledAction)
      .addCase(addDocument.rejected, documentsError)

      .addCase(deleteDocument.fulfilled, deleteDocumentFulfilledAction)

      .addCase(deleteDocumentAssetManager.fulfilled, deleteDocumentFulfilledAction)

      .addCase(validateDocumentSign.fulfilled, validateDocumentSignAction)
      .addCase(validateDocumentSign.pending, setLoadingAction)
      .addCase(validateDocumentSign.rejected, setErrorAction)

      .addCase(validateDocumentSignAssetManager.fulfilled, validateDocumentSignAction)
      .addCase(validateDocumentSignAssetManager.pending, setLoadingAction)
      .addCase(validateDocumentSignAssetManager.rejected, setErrorAction)

      .addCase(getSignatureEmail.rejected, setErrorAction)

      .addCase(updateAdditionalInformation.pending, setLoadingAction)
      .addCase(updateAdditionalInformation.fulfilled, updateAdditionalInfoFulfilledAction)
      .addCase(updateAdditionalInformation.rejected, setErrorAction)

      .addCase(deleteDocumentsInvalidSignatures.fulfilled, defaultFulfilledAction)
      .addCase(deleteDocumentsInvalidSignatures.pending, setLoadingAction)
      .addCase(deleteDocumentsInvalidSignatures.rejected, setErrorAction)

      .addCase(deleteDocumentsInvalidSignaturesAssetManager.fulfilled, defaultFulfilledAction)
      .addCase(deleteDocumentsInvalidSignaturesAssetManager.pending, setLoadingAction)
      .addCase(deleteDocumentsInvalidSignaturesAssetManager.rejected, setErrorAction)

      .addCase(regenerateReports.fulfilled, defaultFulfilledAction)
      .addCase(regenerateReports.pending, setLoadingAction)
      .addCase(regenerateReports.rejected, setErrorAction)

      .addCase(fetchCreatedIdentificationForm.fulfilled, setFormData)
      .addCase(fetchCreatedIdentificationForm.pending, setLoadingAction)
      .addCase(fetchCreatedIdentificationForm.rejected, setErrorAction)

      .addCase(validateBeforeSigning.fulfilled, validateBeforeSigningFulfilled)
      .addCase(validateBeforeSigning.pending, setLoadingAction)
      .addCase(validateBeforeSigning.rejected, setErrorAction)

      .addCase(b2IntegrationAssetManagersPrefill.fulfilled, fulfilledAction)
      .addCase(b2IntegrationAssetManagersPrefill.pending, setLoadingAction)
      .addCase(b2IntegrationAssetManagersPrefill.rejected, setErrorAction)

      .addCase(
        getManagementStructureSyncAssetManagers.fulfilled,
        setManagementStructureSyncAssetManagers
      )
      .addCase(getManagementStructureSyncAssetManagers.pending, setLoadingAction)
      .addCase(getManagementStructureSyncAssetManagers.rejected, setErrorAction)

      .addCase(applicationReport.fulfilled, applicationReportFulfilled)
      .addCase(applicationReport.pending, setLoadingAction)
      .addCase(applicationReport.rejected, setErrorAction)

      .addCase(archiveApplicationReport.pending, applicationReportActionPending)
      .addCase(archiveApplicationReport.fulfilled, applicationReportActionFulfilled)
      .addCase(archiveApplicationReport.rejected, applicationReportActionRejected)

      .addCase(restoreApplicationReport.pending, applicationReportActionPending)
      .addCase(restoreApplicationReport.fulfilled, applicationReportActionFulfilled)
      .addCase(restoreApplicationReport.rejected, applicationReportActionRejected)

      .addCase(updateFormChangeVideoVerification.fulfilled, setFormDataChangeVideoVerification)
      .addCase(updateFormChangeVideoVerification.pending, setLoadingAction)
      .addCase(updateFormChangeVideoVerification.rejected, setErrorAction)

      .addCase(getApplicationReportExcel.fulfilled, getApplicationReportExcelFulfilled)
      .addCase(getApplicationReportExcel.pending, setLoadingAction)
      .addCase(getApplicationReportExcel.rejected, setErrorAction)

      .addCase(getAssetManagerDocs.fulfilled, getAssetManagerDocsFulfilled)
      .addCase(getAssetManagerDocs.rejected, setErrorAction)

      .addCase(generateDocs.fulfilled, setMainDocuments)
      .addCase(generateDocs.rejected, setErrorAction)

      .addCase(getDocumentTemplates.fulfilled, getApplicationReportExcelFulfilled)
      .addCase(getDocumentTemplates.pending, setLoadingAction)
      .addCase(getDocumentTemplates.rejected, setErrorAction)

      .addCase(infoByIban.fulfilled, defaultFulfilledAction)

      .addCase(taxResidencyAdditionalInfoPrefill.fulfilled, taxResidencyPrefillFulfilled)
      .addCase(taxResidencyAdditionalInfoPrefill.pending, setLoadingAction)
      .addCase(taxResidencyAdditionalInfoPrefill.rejected, setErrorAction)

      .addCase(taxResidencyAdditionalInfoDelete.fulfilled, getApplicationReportExcelFulfilled)
      .addCase(taxResidencyAdditionalInfoDelete.pending, setLoadingAction)
      .addCase(taxResidencyAdditionalInfoDelete.rejected, setErrorAction)

      .addCase(updateDataProcessingAcceptedAssetManagers.rejected, setErrorAction)
      .addCase(updateDataProcessingAcceptedAssetManagers.pending, setLoadingAction)
      .addCase(updateDataProcessingAcceptedAssetManagers.fulfilled, defaultFulfilledAction)

      .addCase(checkRegenerateDocs.rejected, setErrorAction)
      .addCase(checkRegenerateDocs.pending, setLoadingAction)
      .addCase(checkRegenerateDocs.fulfilled, defaultFulfilledAction);
  },
});

export const {
  clearForm,
  setIdentificationError,
  setIdentificationLoading,
  updateDocsInFormAction,
  updateFormData,
  setGettingStarted,
  setIsReidentification,
  setApplicationReportTable,
  setApplicationExportToExcel,
  setDisabledDiagramGeneration,
  updateFormDataBack,
  setIdentificationAppId,
  deleteAllAssetManagerDocs,
  deleteAssetManagerDocsById,
  updateAssetManagerVerificationStatus,
} = identificationFormSlice.actions;

export default identificationFormSlice.reducer;
